import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { listPermissionHistory, type PermissionEvent } from "../../../api/permissionHistoryApi";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import { useLoad } from "../../../shared/loading/useLoad";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import { itemKindText } from "../../../features/workflows/inputCards/typeWords";
import { personName, ruleName } from "../settingsTexts";
import { historyLines, type HistoryLine } from "./historyLines";

/**
 * Which permissions existed when in one project (Q-67): every change of a group, its set, its
 * members, an inclusion and the order, the grants of before the switch, and every rule change
 * with its reason, newest first. The project's History tab, for those who manage its people.
 * Review decisions keep counting after a revocation; this says what the reviewer held then.
 */
export default function PermissionHistoryPanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => listPermissionHistory(projectId, signal), [projectId]);
  const { state, reload } = useLoad(load);

  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  if (state.value.length === 0) {
    return <p className="app-text-muted text-sm">{t("permissionHistory.empty")}</p>;
  }
  return (
    <ol className="app-divide-y text-sm">
      {historyLines(state.value).reverse().map((line) => (
        <HistoryEntry key={line.first.eventId} line={line} />
      ))}
    </ol>
  );
}

function HistoryEntry({ line }: { line: HistoryLine }) {
  const { t, i18n } = useTranslation();
  const { first: event, itemKinds } = line;

  return (
    <li className="space-y-0.5 py-2">
      <p>
        {event.person === null ? null : <span className="font-semibold">{personName(t, event.person)} </span>}
        {itemKinds === null ? <EventText event={event} /> : t("permissionHistory.becameKindCurator", { count: itemKinds.length })}
      </p>
      {itemKinds === null ? null : <p className="app-text-muted text-xs">{itemKinds.map((kind) => itemKindText(kind, t)).join(", ")}</p>}
      {event.reason === null || event.reason === "" ? null : <p className="text-xs">{t("permissionHistory.reason", { reason: event.reason })}</p>}
      <p className="app-text-muted text-xs">
        {formatDateTime(event.at, i18n.language)},{" "}
        {event.actor === null ? t("permissionHistory.reconstructed") : t("permissionHistory.by", { name: personName(t, event.actor) })}
      </p>
    </li>
  );
}

function EventText({ event }: { event: PermissionEvent }) {
  const { t } = useTranslation();
  if (event.rules !== null) {
    return event.rules.reason === ""
      ? <>{t("permissionHistory.rulesFromDefaults")}</>
      : <>{t("permissionHistory.rulesChanged", { rules: event.rules.changed.map((name) => ruleName(t, name)).join(", ") })}</>;
  }
  if (event.curatedKinds !== null) {
    const group = event.group?.name ?? "";
    return event.curatedKinds.length === 0
      ? <>{t("permissionHistory.groupCurationTaken", { group })}</>
      : <>{t("permissionHistory.changes.group_curation_changed", { group, kinds: event.curatedKinds.map((kind) => itemKindText(kind, t)).join(", ") })}</>;
  }
  let scope = "";
  if (event.itemKind !== null) {
    const kind = itemKindText(event.itemKind, t);
    scope = event.itemId === null ? t("permissionHistory.everyItemOf", { kind }) : t("permissionHistory.oneItemOf", { kind });
  }
  return (
    <>
      {t(`permissionHistory.changes.${event.change}`, {
        role: event.role === null ? "" : t(`permissionHistory.roles.${event.role}`),
        scope,
        group: event.group?.name ?? "",
      })}
    </>
  );
}
